import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp } from 'lucide-react';

const API = '/api';

/**
 * Compact "Popular categories" strip — supply-side trending signal.
 *
 * Pulls from /api/listings/search/popular-categories which ranks by:
 *   - new listings in the last 30 days (weight ×3)
 *   - total active listings in the category
 *   - floor of ≥5 active listings (no thin categories)
 *   - admin-controlled blocklist
 *
 * Unfakeable by visitors — every output is a real category name created by
 * a moderated business listing. Designed to live directly below the hero
 * search box without a coloured background (transparent on dark heroes).
 */
const PopularCategories = ({ tone = 'dark' }) => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/listings/search/popular-categories`, { params: { limit: 6 } })
      .then((res) => {
        if (cancelled) return;
        setItems(Array.isArray(res.data?.categories) ? res.data.categories : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!items || items.length === 0) return null;

  // Tone controls the text colour so the strip blends with whatever hero
  // background it sits on. `dark` = light text for dark heroes; `light` =
  // dark text for light surfaces.
  const labelClass = tone === 'light' ? 'text-slate-700' : 'text-white/80';
  const linkClass =
    tone === 'light'
      ? 'text-slate-800 hover:text-orange-700 underline-offset-2 hover:underline'
      : 'text-white hover:text-amber-300 underline-offset-2 hover:underline';
  const dotClass = tone === 'light' ? 'text-slate-400' : 'text-white/35';
  const iconClass = tone === 'light' ? 'text-orange-600' : 'text-amber-300';

  return (
    <div
      data-testid="popular-categories-strip"
      className="mt-3 max-w-xl"
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5 text-xs">
        <span className={`inline-flex items-center gap-1.5 font-medium shrink-0 ${labelClass}`}>
          <TrendingUp className={`h-3.5 w-3.5 ${iconClass}`} />
          Popular categories:
        </span>
        <ul className="flex flex-wrap items-center gap-x-1.5 gap-y-1.5">
          {items.map((c, idx) => {
            const inner = (
              <span
                title={`${c.total} active listing${c.total === 1 ? '' : 's'}${c.recent ? ` · ${c.recent} new in last 30 days` : ''}`}
                className="capitalize"
              >
                {c.name}
              </span>
            );
            return (
              <li key={c.name} className="flex items-center gap-1.5">
                {c.slug ? (
                  <Link
                    to={`/business/${c.slug}`}
                    data-testid={`popular-category-${idx}`}
                    className={`font-medium whitespace-nowrap ${linkClass}`}
                  >
                    {inner}
                  </Link>
                ) : (
                  <span data-testid={`popular-category-${idx}`} className={`font-medium whitespace-nowrap ${labelClass}`}>
                    {inner}
                  </span>
                )}
                {idx < items.length - 1 && (
                  <span className={dotClass} aria-hidden>·</span>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PopularCategories;
